import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

function RefundResolution() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // Start at 0 for details collection
  const [fullName, setFullName] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [email, setEmail] = useState('');
  const [assistant, setAssistant] = useState('');
  const [purchaseYear, setPurchaseYear] = useState('');
  const [receivedProduct, setReceivedProduct] = useState('');
  const [proofFile, setProofFile] = useState(null);
  const [contactMethod, setContactMethod] = useState('');
  const [contactInfo, setContactInfo] = useState('');
  const [showOutcome, setShowOutcome] = useState('');
  const [resolutionType, setResolutionType] = useState(''); // 'activation' or 'migration'

  const handleDetailsSubmit = () => {
    if (!fullName || !contactNumber) {
      alert('Please fill in all required fields (Name and Contact Number)');
      return;
    }
    setStep(1);
  };

  const handleYearSelect = (year) => {
    setPurchaseYear(year);
    
    if (year === '2021-earlier') {
      setResolutionType('migration');
      setShowOutcome('resolution-decision');
    } else {
      setStep(2);
    }
  };

  const handleDeliveryResponse = (response) => {
    setReceivedProduct(response);
    
    if (response === 'yes' || response === 'received-not-used') {
      setResolutionType('activation');
      setShowOutcome('resolution-decision');
    } else if (response === 'no') {
      setStep(3);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProofFile(file);
    }
  };

  const handleSubmit = async () => {
    if (!proofFile || !contactMethod || !contactInfo) {
      alert('Please complete all required fields');
      return;
    }

    // Here you would typically send this to your backend
    console.log({
      purchaseYear,
      receivedProduct,
      proofFile: proofFile.name,
      contactMethod,
      contactInfo
    });

    setShowOutcome('submitted');
  };

  const resetFlow = () => {
    setStep(0);
    setFullName('');
    setContactNumber('');
    setEmail('');
    setAssistant('');
    setPurchaseYear('');
    setReceivedProduct('');
    setProofFile(null);
    setContactMethod('');
    setContactInfo('');
    setShowOutcome('');
    setResolutionType('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <button 
            onClick={() => navigate('/')}
            className="text-sm text-gray-600 hover:text-black mb-4 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </button>
          <h1 className="text-3xl font-light text-gray-900">Customer Support & Purchase Verification</h1>
          <p className="text-gray-600 mt-2">
            Please complete the steps below so we can verify your purchase and assist you with the correct resolution.
            <br />
            <span className="text-sm text-gray-500">This process takes less than 2 minutes.</span>
          </p>
        </div>
      </div>

      {/* Progress Bar */}
      {!showOutcome && (
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-4xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 0 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className={`text-sm font-medium ${step >= 0 ? 'text-gray-900' : 'text-gray-400'}`}>Your Details</span>
              </div>
              <div className="flex-1 h-1 bg-gray-200 mx-4">
                <div className={`h-full bg-blue-600 transition-all duration-300`} style={{ width: step >= 1 ? '100%' : '0%' }}></div>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                  1
                </div>
                <span className={`text-sm font-medium ${step >= 1 ? 'text-gray-900' : 'text-gray-400'}`}>Purchase Year</span>
              </div>
              <div className="flex-1 h-1 bg-gray-200 mx-4">
                <div className={`h-full bg-blue-600 transition-all duration-300`} style={{ width: step >= 2 ? '100%' : '0%' }}></div>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                  2
                </div>
                <span className={`text-sm font-medium ${step >= 2 ? 'text-gray-900' : 'text-gray-400'}`}>Delivery Status</span>
              </div>
              <div className="flex-1 h-1 bg-gray-200 mx-4">
                <div className={`h-full bg-blue-600 transition-all duration-300`} style={{ width: step >= 3 ? '100%' : '0%' }}></div>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                  3
                </div>
                <span className={`text-sm font-medium ${step >= 3 ? 'text-gray-900' : 'text-gray-400'}`}>Verification</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        
        {/* STEP 0: Customer Details */}
        {step === 0 && !showOutcome && (
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Your Details</h2>
            <p className="text-gray-600 mb-6">Please complete all fields so we can assist you properly</p>
            
            <div className="space-y-6">
              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Enter your full name"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {/* Contact Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Number (WhatsApp preferred) <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={contactNumber}
                  onChange={(e) => setContactNumber(e.target.value)}
                  placeholder="+27 XXX XXX XXXX"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Include country code (e.g., +27 for South Africa)</p>
              </div>

              {/* Email Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Who Assisted You */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Who assisted you when you purchased the product?
                </label>
                <select
                  value={assistant}
                  onChange={(e) => setAssistant(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="">Select an option</option>
                  <option value="john-ghatti">John Ghatti (Owner)</option>
                  <option value="sales-team">Sales Team Member</option>
                  <option value="website">Website (Self-Service)</option>
                  <option value="social-media">Social Media</option>
                  <option value="referral">Friend/Referral</option>
                  <option value="other">Other</option>
                  <option value="dont-remember">Don't Remember</option>
                </select>
              </div>

              {/* Submit Button */}
              <button
                onClick={handleDetailsSubmit}
                disabled={!fullName || !contactNumber}
                className={`w-full py-4 rounded-xl font-semibold text-white transition-all ${
                  fullName && contactNumber
                    ? 'bg-blue-600 hover:bg-blue-700 shadow-lg hover:shadow-xl'
                    : 'bg-gray-300 cursor-not-allowed'
                }`}
              >
                Continue to Verification
              </button>

              <p className="text-xs text-gray-500 text-center">
                <span className="text-red-500">*</span> Required fields
              </p>
            </div>
          </div>
        )}

        {/* STEP 1: Purchase Year */}
        {step === 1 && !showOutcome && (
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">When did you purchase your product?</h2>
            <p className="text-gray-600 mb-6">Select the year you made your purchase</p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {['2026', '2025', '2024', '2023', '2022', '2021 or earlier'].map((year) => (
                <button
                  key={year}
                  onClick={() => handleYearSelect(year === '2021 or earlier' ? '2021-earlier' : year)}
                  className="p-4 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all text-center font-medium text-gray-700 hover:text-blue-600"
                >
                  {year}
                </button>
              ))}
            </div>

            <button
              onClick={() => setStep(0)}
              className="mt-6 text-sm text-gray-500 hover:text-gray-700 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to your details
            </button>
          </div>
        )}

        {/* STEP 2: Product Delivery */}
        {step === 2 && !showOutcome && (
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Did you receive your product?</h2>
            <p className="text-gray-600 mb-6">Please select the option that best describes your situation</p>
            
            <div className="space-y-4">
              <button
                onClick={() => handleDeliveryResponse('yes')}
                className="w-full p-6 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
              >
                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 rounded-full border-2 border-gray-300 flex-shrink-0 mt-1"></div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Yes — I received and/or used the robot</div>
                    <div className="text-sm text-gray-600">I have accessed or used the product</div>
                  </div>
                </div>
              </button>

              <button
                onClick={() => handleDeliveryResponse('received-not-used')}
                className="w-full p-6 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
              >
                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 rounded-full border-2 border-gray-300 flex-shrink-0 mt-1"></div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">I received it but never used it</div>
                    <div className="text-sm text-gray-600">Product was delivered but I haven't used it yet</div>
                  </div>
                </div>
              </button>

              <button
                onClick={() => handleDeliveryResponse('no')}
                className="w-full p-6 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
              >
                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 rounded-full border-2 border-gray-300 flex-shrink-0 mt-1"></div>
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">No — I never received the robot</div>
                    <div className="text-sm text-gray-600">I did not receive access to the product</div>
                  </div>
                </div>
              </button>
            </div>

            <button
              onClick={() => setStep(1)}
              className="mt-6 text-sm text-gray-500 hover:text-gray-700 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to previous step
            </button>
          </div>
        )}

        {/* STEP 3: Upload Proof */}
        {step === 3 && !showOutcome && (
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Upload Proof of Purchase</h2>
            <p className="text-gray-600 mb-6">Please upload one of the following documents to verify your purchase</p>
            
            <div className="space-y-6">
              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Document (Receipt, Invoice, Bank Statement, or Email Confirmation)
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-500 transition-colors">
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="file-upload"
                  />
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <div className="text-blue-600 font-medium mb-1">Click to upload</div>
                    <div className="text-sm text-gray-500">PDF, JPG, or PNG (max 10MB)</div>
                    {proofFile && (
                      <div className="mt-4 text-sm text-green-600 font-medium">
                        ✓ {proofFile.name} uploaded
                      </div>
                    )}
                  </label>
                </div>
              </div>

              {/* Contact Method */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Preferred Contact Method
                </label>
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { value: 'whatsapp', label: 'WhatsApp', icon: '📱' },
                    { value: 'email', label: 'Email', icon: '✉️' },
                    { value: 'phone', label: 'Phone', icon: '📞' }
                  ].map((method) => (
                    <button
                      key={method.value}
                      onClick={() => setContactMethod(method.value)}
                      className={`p-4 border-2 rounded-xl transition-all ${
                        contactMethod === method.value
                          ? 'border-blue-500 bg-blue-50 text-blue-600'
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <div className="text-2xl mb-2">{method.icon}</div>
                      <div className="text-sm font-medium">{method.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Contact Info */}
              {contactMethod && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {contactMethod === 'whatsapp' && 'WhatsApp Number'}
                    {contactMethod === 'email' && 'Email Address'}
                    {contactMethod === 'phone' && 'Phone Number'}
                  </label>
                  <input
                    type={contactMethod === 'email' ? 'email' : 'tel'}
                    value={contactInfo}
                    onChange={(e) => setContactInfo(e.target.value)}
                    placeholder={
                      contactMethod === 'whatsapp' ? '+27 XXX XXX XXXX' :
                      contactMethod === 'email' ? 'your@email.com' :
                      '+27 XXX XXX XXXX'
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              )}

              {/* Submit Button */}
              <button
                onClick={handleSubmit}
                disabled={!proofFile || !contactMethod || !contactInfo}
                className={`w-full py-4 rounded-xl font-semibold text-white transition-all ${
                  proofFile && contactMethod && contactInfo
                    ? 'bg-blue-600 hover:bg-blue-700 shadow-lg hover:shadow-xl'
                    : 'bg-gray-300 cursor-not-allowed'
                }`}
              >
                Submit for Review
              </button>
            </div>

            <button
              onClick={() => setStep(2)}
              className="mt-6 text-sm text-gray-500 hover:text-gray-700 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to previous step
            </button>
          </div>
        )}

        {/* OUTCOME: Resolution Decision */}
        {showOutcome === 'resolution-decision' && (
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Resolution Outcome</h2>
            
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
              <p className="text-gray-800 mb-4">Based on your submission, the applicable resolution is:</p>
              
              <div className="flex items-start gap-3 bg-white rounded-lg p-4 mb-4">
                <svg className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="font-semibold text-gray-900 mb-1">
                    {resolutionType === 'migration' ? 'Migration to Kasango 1.0' : 'System Activation / Re-activation'}
                  </p>
                  <p className="text-sm text-gray-600">
                    (Refunds do not apply to digital products once delivered or accessed.)
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <p className="text-gray-800 font-medium mb-4">Do you accept this resolution?</p>
              
              <div className="space-y-3">
                <button
                  onClick={() => setShowOutcome('resolution-accepted')}
                  className="w-full p-5 border-2 border-green-500 bg-green-50 rounded-xl hover:bg-green-100 transition-all text-left flex items-center gap-4"
                >
                  <div className="w-6 h-6 rounded-full border-2 border-green-600 flex items-center justify-center flex-shrink-0">
                    <div className="w-3 h-3 rounded-full bg-green-600"></div>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Yes — Proceed with {resolutionType === 'migration' ? 'Migration' : 'Activation'} / Support</div>
                    <div className="text-sm text-gray-600">I accept this resolution and would like assistance</div>
                  </div>
                </button>

                <button
                  onClick={() => setShowOutcome('resolution-rejected')}
                  className="w-full p-5 border-2 border-gray-300 hover:border-red-400 hover:bg-red-50 rounded-xl transition-all text-left flex items-center gap-4"
                >
                  <div className="w-6 h-6 rounded-full border-2 border-gray-400 flex items-center justify-center flex-shrink-0">
                    <div className="w-3 h-3 rounded-full bg-white"></div>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">No — I Reject This Resolution</div>
                    <div className="text-sm text-gray-600">I do not accept the offered resolution</div>
                  </div>
                </button>
              </div>
            </div>

            <button
              onClick={resetFlow}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Start over
            </button>
          </div>
        )}

        {/* OUTCOME: Resolution Accepted */}
        {showOutcome === 'resolution-accepted' && (
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">Thank you for accepting</h2>
                <p className="text-gray-700 leading-relaxed">
                  Our support team will contact you shortly to assist with your {resolutionType === 'migration' ? 'migration to Kasango 1.0' : 'system activation'}.
                </p>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
              <p className="text-sm text-gray-700 mb-3"><strong>What happens next?</strong></p>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>✓ Support team will reach out within 24 hours</li>
                <li>✓ {resolutionType === 'migration' ? 'We\'ll help you migrate to Kasango 1.0' : 'We\'ll help you activate your system'}</li>
                <li>✓ Full technical assistance provided</li>
              </ul>
            </div>

            <div className="flex gap-4">
              <a
                href={resolutionType === 'migration' 
                  ? "https://wa.me/27685358462?text=Hi%2C%20I%20accepted%20the%20migration%20to%20Kasango%201.0%20resolution"
                  : "https://wa.me/27685358462?text=Hi%2C%20I%20accepted%20the%20activation%20support%20resolution"}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl text-center transition-all"
              >
                Chat with Support Now
              </a>
              <button
                onClick={() => navigate('/')}
                className="flex-1 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-xl transition-all"
              >
                Return to Home
              </button>
            </div>
          </div>
        )}

        {/* OUTCOME: Resolution Rejected - CASE CLOSED */}
        {showOutcome === 'resolution-rejected' && (
          <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-red-200">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-red-900 mb-3">Resolution Declined — Case Closed</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Thank you for your submission.
                </p>
              </div>
            </div>

            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 mb-6">
              <p className="text-gray-800 leading-relaxed mb-4">
                As stated in our <strong>Refund Policy</strong>, all digital software purchases are <strong>final and non-refundable</strong> once delivered or accessed.
              </p>
              <p className="text-gray-800 leading-relaxed mb-4">
                Since the offered resolution has been declined, <strong>no further action can be taken</strong> on this request and the case is now <strong>formally closed</strong>.
              </p>
              <p className="text-gray-700 leading-relaxed">
                If you require activation or technical support in the future, our team will still be available to assist you under normal support procedures.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => navigate('/')}
                className="flex-1 py-3 bg-gray-900 hover:bg-black text-white font-semibold rounded-xl transition-all"
              >
                Exit Portal
              </button>
              <a
                href="https://wa.me/27685358462?text=Hi%2C%20I%20need%20technical%20support"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-xl text-center transition-all"
              >
                Contact Support (Technical Only)
              </a>
            </div>

            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <p className="text-xs text-gray-700">
                  <strong>Final Notice:</strong> This case has been officially closed. No refund will be issued. This decision is final and in accordance with our published terms of service.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* OUTCOME: Old Purchase (REMOVED - now goes to resolution-decision) */}

        {/* OUTCOME: Product Delivered (REMOVED - now goes to resolution-decision) */}

        {/* OUTCOME: Submitted */}
        {showOutcome === 'submitted' && (
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">Thank you — your case has been submitted</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Our support team will verify delivery status and assist you with access.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>Please note:</strong> Refunds do not apply, however we will ensure you receive your product.
                </p>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
              <p className="text-sm text-gray-700 mb-2"><strong>What happens next?</strong></p>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>✓ We'll review your proof of purchase within 24 hours</li>
                <li>✓ Our team will contact you via {contactMethod === 'whatsapp' ? 'WhatsApp' : contactMethod === 'email' ? 'email' : 'phone'}</li>
                <li>✓ We'll provide access to your purchased product</li>
              </ul>
            </div>

            <div className="flex gap-4">
              <a
                href="https://wa.me/27685358462"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl text-center transition-all"
              >
                Chat with Support
              </a>
              <button
                onClick={() => navigate('/')}
                className="flex-1 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-xl transition-all"
              >
                Return to Home
              </button>
            </div>
          </div>
        )}

      </div>

      {/* Legal Footer */}
      <div className="bg-gray-900 text-white py-8 mt-12">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="text-sm font-medium mb-2">Legal Notice</p>
              <p className="text-xs text-gray-400 leading-relaxed">
                All digital software purchases are final. Refunds do not apply once access, download, or delivery has been provided. 
                Submitting false information will void your request. By proceeding, you acknowledge that you have read and agree to these terms.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RefundResolution;
